import { trace } from 'rytm-helpers'
import gsap from 'gsap';

/**
 * Prototype of r-roll slider
 * Public Events:
 * rroll.slide - fired when slide changes
 * rroll.slid - fired when slide animation is finished
 * 
 */
/**
 * #########################
 * ### State definition ####
 * #########################
 */
const defaultState = {
  id: null,
  oryginalHtml: null,
  initialized: false,
  index: 0,
  itemsTotal: 0,
  stage: null,
  stageInner: null,
  currentItem: null,
  currentItemX: 0,
  autoPlay: true,
  autoPlayTimer: null,
  autoPlayOn: true,
  // pauseOnHover: true,
  timing: {
    transition: 400,
    autoPlay: 5000,
    ease: 'power2.inOut',
    animating: false,
  },
  responsive: {
    0: {
      items: 1,
      openWidth: .8, // % of viewport width
      offsetX: .1, // % of viewport width
    },
    576: {
      items: 3,
      openWidth: .55, // % of viewport width
      offsetX: .18, // % of viewport width
    },
    992: {
      items: 5,
      openWidth: .42, // % of viewport width
      offsetX: .18, // % of viewport width
    },
  },
  classNames: {
    ready: 'r-roll-ready',
    item: 'r-roll-item', // el.rState.classNames.item
    itemCurrent: 'r-roll-item-current', // el.rState.classNames.itemCurrent
    sibling1: 'r-roll-sibling-1',
    sibling2: 'r-roll-sibling-2',
    sibling3: 'r-roll-sibling-3',
    sibling4: 'r-roll-sibling-4',
    sibling5: 'r-roll-sibling-5',
  },
  clones: [],
  widths: {
    item: 0,
    itemOpen: 0,
    offset: 0,
  },
  toucheMinDistX: 50,
  touches: {
    x: 0,
    y: 0,
  },
}
// Global state definition
let globalState = {
  initialized: false,
  classNames: {
    instance: 'r-roll-instance',
    stage: 'r-roll-stage',
    stageInner: 'r-roll-stage-inner',
    navNext: 'r-roll-next',
    navPrev: 'r-roll-prev',
    navPause: 'r-roll-pause',
    pauseActive: 'r-roll-pause-active',
  }
};
// r-roll elements {id: el}
let rolls = {}; 

/**
 * #######################
 * ### Public methods ####
 * #######################
 */
// ### initialize
export const initRRoll = (el, params = {}) => {
  if (el?.rState?.initialized) {
    console.error("r-roll already initiated", el);
    return;
  }
  const dataParams = getElDataParams(el);
  el.rState = {
    ...defaultState,
    ...dataParams,
    ...params,
    id: Object.keys(rolls).length,
    oryginalHtml: el.innerHTML,
    initialized: true,
  };
  buildStage(el);
  // prepare clones
  prepareElements(el);
  // set width, etc.
  updateStage(el);
  // add class ready and instance class names
  el.classList.add(el.rState.classNames.ready);
  el.classList.add(globalState.classNames.instance);
  // add to rolls repository
  rolls[el.rState.id] = el;
  // start autoplay
  startAutoPlay(el);
  // # global events
  if (!globalState.initialized) {
    addGlobalEventListeners();
    globalState.initialized = true;
  }
}
// ### destroy
export const destroyRRoll = (el) => {
  if (!el?.rState?.initialized) {
    console.error("r-roll was not initiated", el);
    return;
  }
  stopAutoPlay(el);
  // remove class names
  // add class ready and instance class names
  el.classList.remove(el.rState.classNames.ready);
  el.classList.remove(globalState.classNames.instance);
  // replace HTML with oryginal
  el.innerHTML = el.rState.oryginalHtml;
  // remove from rolls
  delete rolls[el.rState.id];
  // reset el state
  el.rState = {
    ...defaultState
  }
  // # global events (remove?)
  if (Object.keys(rolls).length === 0) {
    removeGlobalEventListeners();
    globalState.initialized = false;
  }
}

/**
 * ##############################
 * ### R-roll Event handlers ####
 * ##############################
 */
// NEXT click event handler
const onRrollNextClick = (e) => {
  e.preventDefault();
  const el = getRrollEventTarget(e);
  if (el) {
    openToNext(el, el.rState.index + 1);
  }
}
// PREV click event handler
const onRrollPrevClick = (e) => {
  e.preventDefault();
  const el = getRrollEventTarget(e);
  if (el) {
    openToPrev(el, el.rState.index - 1);
  }
}
// PAUSE click event handler
const onRrollPauseClick = (e) => {
  e.preventDefault();
  const el = getRrollEventTarget(e);
  if (el) {
    togglePause(el, e.target);
  }
}
// evetn listeners for elements with class names
const classMethodsClick = [
  {
    className: globalState.classNames.navNext,
    eventHandler: onRrollNextClick,
  },
  {
    className: globalState.classNames.navPrev,
    eventHandler: onRrollPrevClick,
  },
  {
    className: globalState.classNames.navPause,
    eventHandler: onRrollPauseClick,
  },
];

/**
 * ######################
 * ### Global Events ####
 * ######################
 */
// ### Global event listeners ###
const onDocumentClick = (e) => {
  classMethodsClick.forEach((eo) => {
    if (e.target.classList.contains(eo.className)) {
      eo.eventHandler(e);
      return;
    }
  })
}
const onWindowResize = (e) => {
  Object.values(rolls).forEach((el) => {
    updateStage(el);
  });
}
const onDocumentTouchStart = (e) => {
  const el = getRrollEventTarget(e);
  if (el) {
    el.rState.touches.x = e.touches[0].clientX;
    el.rState.touches.y = e.touches[0].clientY;
  }
}
const onDocumentTouchMove = (e) => {
  const el = getRrollEventTarget(e);
  if (el) {
    const dx = e.touches[0].clientX - el.rState.touches.x;
    const dy = e.touches[0].clientY - el.rState.touches.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > el.rState.toucheMinDistX){
      if (dx > 0) {
        openToPrev(el, el.rState.index - 1);
      } else {
        openToNext(el, el.rState.index + 1);
      }
    }
  }
}

// Add global event listeners
const addGlobalEventListeners = () => {
  trace("# add global events");
  // window resize
  window.addEventListener('resize', onWindowResize);
  // clicks & hovers
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('touchstart', onDocumentTouchStart);
  document.addEventListener('touchmove', onDocumentTouchMove);
}
// Remove global event listeners
const removeGlobalEventListeners = () => {
  trace("# remove global events");
  // window resize
  window.removeEventListener('resize', onWindowResize);
  // clicks & hovers
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('touchstart', onDocumentTouchStart);
  document.removeEventListener('touchmove', onDocumentTouchMove);
}

/**
 * ####################
 * ### R-roll logic ###
 * ####################
 */
const togglePause = (el, trigger) => {
  el.rState.autoPlayOn = !el.rState.autoPlayOn;
  if (el.rState.autoPlayOn) {
    startAutoPlay(el);
    trigger.classList.remove(globalState.classNames.pauseActive);
  } else {
    stopAutoPlay(el);
    trigger.classList.add(globalState.classNames.pauseActive);
  }
}
const startAutoPlay = (el) => {
  if (!el.rState.autoPlay || !el.rState.autoPlayOn) {
    return;
  }
  stopAutoPlay(el);
  el.rState.autoPlayTimer = setTimeout(() => {
    openToNext(el, el.rState.index + 1);
    startAutoPlay(el);
  }, el.rState.timing.autoPlay);
}
const stopAutoPlay = (el) => {
  clearTimeout(el.rState.autoPlayTimer);
}

/**
 * Open to NEXT index
 * @param {HTMLElement} el
 * @param {Number} index
 */
const openToNext = (el, index) => {
  if (index >= el.rState.itemsTotal) {
    index = 0;
  }
  openToItem(el, getNextItemByIndex(el, index));
}
/**
 * Open to PREV index
 * @param {HTMLElement} el
 * @param {Number} index
 */
const openToPrev = (el, index) => {
  if (index < 0) {
    index = el.rState.itemsTotal - 1;
  }
  openToItem(el, getPrevItemByIndex(el, index));
}
/**
 * Open to item
 * @param {HTMLElement} el
 * @param {HTMLElement} nextItem
 */
const openToItem = (el, nextItem) => {
  if (el.rState.timing.animating) {
    return false;
  }
  el.rState.timing.animating = true;
  stopAutoPlay(el);
  // collect data
  const prevIndex = el.rState.index;
  const prevItem = el.rState.currentItem;
  const prevItemX = el.rState.currentItemX;
  const nextIndex = parseInt(nextItem.dataset.index);
  const newItemX = getNewItemX(el, nextItem);
  const direction = newItemX > prevItemX ? 1 : -1;
  // dispatch slide event
  el.dispatchEvent(new CustomEvent('rroll.slide', { bubbles: true, detail: { 
    index: prevIndex,
    currentItem: prevItem,
    currentItemX: prevItemX,
    nextIndex,
    nextItem,
    nextItemX: newItemX,
    direction,
  } }));
  // update class names + state
  el.rState.currentItem.classList.remove(el.rState.classNames.itemCurrent);
  nextItem.classList.add(el.rState.classNames.itemCurrent);
  updateSiblingClasses(el);
  el.rState.currentItem = nextItem;
  el.rState.currentItemX = newItemX;
  el.rState.index = nextIndex;
  // animations
  const duration = el.rState.timing.transition / 1000;
  const ww = getWindowWidth();
  const prevStageX = -prevItemX + (ww / 2) - (el.rState.widths.itemOpen / 2);
  const newStageX = -newItemX + (ww / 2) - (el.rState.widths.itemOpen / 2);
  const ease = el.rState.timing.ease;

  // trace("distance X:", Math.round(newItemX - prevItemX), "new stage X:", newStageX);
  // trace("^ openToItem ^ current item X", el.rState.currentItemX);

  // kill any ongoing nimation
  gsap.killTweensOf(el.rState.stageInner, prevItem, nextItem);
  // set stageInner initial position
  gsap.set(el.rState.stageInner, { x: prevStageX });
  // animate width of prev (current active) item
  gsap.to(prevItem, {
    duration,
    width: el.rState.widths.item,
    ease,
  });
  gsap.to(nextItem, {
    duration,
    width: el.rState.widths.itemOpen,
    ease,
  });
  gsap.to(el.rState.stageInner, {
    duration,
    x: newStageX,
    ease,
    onInterrupt: () => {
      trace("animation interrupted");
    },
    onComplete: () => {
      // dispatch slid event
      el.dispatchEvent(new CustomEvent('rroll.slid', { bubbles: true, detail: { 
        index: el.rState.index,
        currentItem: el.rState.currentItem,
        currentItemX: el.rState.currentItemX,
        prevIndex,
        prevItem,
        prevItemX,
        direction,
      } }));
      // update stage
      updateStage(el);
      // start autoplay
      startAutoPlay(el);
      el.rState.timing.animating = false;
    }
  });
}
// add sibling-1, sibling-2, ... classes
const updateSiblingClasses = (el) => {
  const list = getItems(el);
  let itemsBefore = [];
  let itemsAfter = [];
  let currentFound = false;
  list.forEach((it) => {
    // remove sibling classes
    for (let i = 1; i <= 5; i++) {
      it.classList.remove(el.rState.classNames[`sibling${i}`]);
    }
    if (it.classList.contains(el.rState.classNames.itemCurrent)) {
      currentFound = true;
    } else if (!currentFound) {
      itemsBefore.push(it);
    } else {
      itemsAfter.push(it);
    }
  });
  itemsAfter.forEach((it, index) => {
    if (index < 5) {
      it.classList.add(el.rState.classNames[`sibling${index + 1}`]);
    }
  });
  itemsBefore.reverse().forEach((it, index) => {
    if (index < 5) {
      it.classList.add(el.rState.classNames[`sibling${index + 1}`]);
    }
  });

}

// get the new current item X position
const getNewItemX = (el, newItem) => {
  const list = getItems(el);
  let x = 0;
  let found = false;
  list.forEach((it) => {
    if (it.isSameNode(newItem)) {
      found = true;
    }
    if (!found) {
      x += el.rState.widths.item;
    }
  });
  return x;
}
// find the next item occuring after the current item
const getNextItemByIndex = (el, index) => {
  const list = getItems(el);
  let nextItem = null;
  let currentFound = false;
  list.forEach((it) => {
    if (it.classList.contains(el.rState.classNames.itemCurrent)) {
      currentFound = true;
    }
    if (currentFound && !nextItem && parseInt(it.dataset.index) === index) {
      nextItem = it;
    }
  });
  return nextItem;
}
// find the prev item occuring before the current item
const getPrevItemByIndex = (el, index) => {
  const list = getItems(el);
  let prevItem = null;
  let currentFound = false;
  list.forEach((it) => {
    if (!currentFound && parseInt(it.dataset.index) === index) {
      prevItem = it;
    }
    if (it.classList.contains(el.rState.classNames.itemCurrent)) {
      currentFound = true;
    }
  });
  return prevItem;
}

/**
 * Update stage positions and sizes
 * @param {HTMLElement} el 
 */
const updateStage = (el) => {
  // distribute items on both sides
  distributeItems(el);
  // set item width
  setWidths(el);
  // set stage position
  setStagePosition(el);
}
/**
 * Set stage inner position
 * @param {HTMLElement} el 
 */
const setStagePosition = (el) => {
  const ww = getWindowWidth();
  const offset = -el.rState.currentItemX + (ww / 2) - (el.rState.widths.itemOpen / 2);
  el.rState.stageInner.style.transform = `translateX(${offset}px)`;
}
/**
 * Set width of items
 * @param {HTMLElement} el 
 */
const setWidths = (el) => {
  // calculate widhs and store them in el rState
  el.rState.widths = getWidths(el);
  const list = getItems(el);  
  const stageInnerWidth = el.rState.widths.item * (list.length - 1) + el.rState.widths.itemOpen;
  el.rState.stageInner.style.width = `${stageInnerWidth}px`;
  let x = 0;
  list.forEach((it, index) => {
    let itemWidth = el.rState.widths.item;
    if (it.classList.contains(el.rState.classNames.itemCurrent)) {
      itemWidth = el.rState.widths.itemOpen
      el.rState.currentItemX = x;
      // trace("* setWidths * current item X:", x);
    } 
    it.style.width = `${itemWidth}px`;
    x += itemWidth;
  });

}
const getWidths = (el) => {
  const ww = getWindowWidth();
  const responsive = getResponsive(el);
  const itemOpen = ww * responsive.openWidth;
  const offset = ww * responsive.offsetX;
  const space = ww - itemOpen + 2 * offset;
  const item =  Math.round(space / responsive.items * 100) / 100;
  return {
    item,
    itemOpen,
    offset,
  }
}
/**
 * Distribute items - using clones 
 * keep same number of items on 
 * both sides of the current item
 * @param {HTMLElement} el 
 */
const distributeItems = (el) => {
  // how many items are before current
  let beforeNum = 0;
  // how many items are after current
  let afterNum = 0;
  // we need to have the same number of items on both sides
  let currentFound = false;
  const list = getItems(el);
  list.forEach((it, index) => {
    if (it.classList.contains(el.rState.classNames.itemCurrent)) {
      currentFound = true;
    } else if (!currentFound) {
      beforeNum++;
    } else {
      afterNum++;
    }
  });
  const moveBeforeNum = afterNum > beforeNum ? Math.floor((afterNum - beforeNum) / 2) : 0;
  const moveAfterNum = afterNum < beforeNum ? Math.floor((beforeNum - afterNum) / 2) : 0;
  for (let i = 0; i < moveAfterNum; i++) {
    const it = list.shift();
    it.parentNode.insertBefore(it, list[list.length - 1].nextSibling);
  }
  let listBefore = [];
  for (let i = 0; i < moveBeforeNum; i++) {
    const it = list.pop();
    listBefore.push(it);
  }
  listBefore.reverse();
  listBefore.forEach((it) => {
    it.parentNode.insertBefore(it, list[0]);
  });
  updateSiblingClasses(el);
};

/**
 * Build stage
 * @param {HTMLElement} el 
 */
const buildStage = (el) => {
  // create outer wrapper
  el.rState.stage = document.createElement('div');
  el.rState.stage.classList.add(globalState.classNames.stage);
  el.rState.stageInner = document.createElement('div');
  el.rState.stageInner.classList.add(globalState.classNames.stageInner);
  el.rState.stageInner.innerHTML = el.innerHTML;
  el.innerHTML = '';
  el.rState.stage.appendChild(el.rState.stageInner);
  el.appendChild(el.rState.stage);
  const list = getItems(el);
  el.rState.itemsTotal = list.length;
  list.forEach((it, index) => {
    it.classList.add(el.rState.classNames.item);
    it.dataset.index = index;
    if (index === el.rState.index) {
      it.classList.add(el.rState.classNames.itemCurrent);
      el.rState.currentItem = it;
    }
  });
}
/**
 * Prepare clones for carousel
 * @param {HTMLElement} el 
 */
const prepareElements = (el) => {
  const minItemsNum = getMaxItemsNum(el) + 2;
  let totalClones = 0;
  while (totalClones < minItemsNum) {
    totalClones += el.rState.itemsTotal;
  }
  const list = getItems(el);
  el.rState.clones = [];
  let cc = 0;
  for (let i = 0; i < totalClones; i++) {
    const clone = list[cc].cloneNode(true);
    clone.classList.add('r-roll-clone');
    clone.classList.remove(el.rState.classNames.itemCurrent);
    clone.dataset.index = cc;
    el.rState.stageInner.appendChild(clone);
    el.rState.clones.push(clone);
    cc++;
    if (cc >= list.length) {
      cc = 0;
    }
  }
}

// ################
// ### Helpers ####

/**
 * Get rroll el by event (e.g. click)
 * @param {Event} e
 * @return {HTMLElement}
 */
const getRrollEventTarget = (e) => {
  const selector = e?.target?.getAttribute("href") ? e.target.getAttribute("href") : e?.target?.dataset?.rrollTarget;
  // check if selector is valid selector string

  const el = selector && (selector.charAt(0) == "#" || selector.charAt(0) == ".") ? document.documentElement.querySelector(selector) : null;
  let id = el ? el?.getAttribute("id") : null;
  if (!id) {
    // search for parent r-roll instance
    id = e?.target?.closest(`.${ globalState.classNames.instance }`)?.getAttribute("id");
  }
  return id ? getRrollInstanceById(id) : null;
}
/**
 * Get rroll instance by ID
 * @param {String} id
 * @return {HTMLElement}
 */
const getRrollInstanceById = (id) => {
  let result = null;
  Object.values(rolls).forEach((el) => {
    if (el.getAttribute("id") === id) {
      result = el;
      return el;
    }
  });
  return result;
}
/**
 * Get array of list items
 * @param {HTMLElement} el 
 */
const getItems = (el) => {
  return [...el.querySelectorAll(`.${ globalState.classNames.stageInner } > *`)];
}
/**
 * Get max items number (from all responsive settings)
 * @param {HTMLElement} el 
 * @return {Number}
 */
const getMaxItemsNum = (el) => {
  return Math.max(...Object.values(el.rState.responsive).map((r) => r.items)); 
}
/**
 * Get responsive settings for current viewport width
 * @param {HTMLElement} el 
 * @return {Object}
 */
const getResponsive = (el) => {
  const ww = getWindowWidth();
  const responsive = el.rState.responsive;
  const keys = Object.keys(responsive).map(Number).sort((a, b) => a - b);
  let result = responsive[keys[0]];
  keys.forEach((key) => {
    if (ww >= key) {
      result = responsive[key];
    }
  });
  return result;
}
/**
 * Get data set params
 * @param {HTMLElement} el 
 * @return {Object}
 */
const getElDataParams = (el) => {
  const dataParams = {};
  Object.keys(el.dataset).forEach((key) => {
    if (key.startsWith('rroll')) {
      const newKey = key.replace('rroll', '').charAt(0).toLowerCase() + key.slice(6);
      dataParams[newKey] = parseDataValue(el.dataset[key]);
    }
  });
  return dataParams;
}
const parseDataValue = (val) => {
  if (val === 'true') {
    return true;
  } else if (val === 'false') {
    return false;
  } else if (!isNaN(val)) {
    return parseInt(val);
  }
  return val;
}
const getWindowWidth = () => {
  return window.visualViewport?.width || window.innerWidth;
}